import type {
  AppSpecFull,
  ApplicationPartDecision,
  ApplicationPartKind,
  GenerationResponsibility,
  StudioApplicationAssetQualityEvidence,
  StudioApplicationDatasourceQualityEvidence,
  StudioApplicationGenerationQuality,
} from '@/services/block-api-service'
import type {
  StudioEndUserAppBrowserEvidence,
  StudioEndUserAppGenerationProof,
} from '@/features/validation/studio-generation-evidence'
import { validateActionFlowCompilerArtifacts } from './actionflow-binding-plan'
import { validateActionFlowTopologyContract } from './actionflow-topology'
import {
  type BuildStudioOntologyRetrievalTraceOptions,
  buildStudioOntologyRetrievalTrace,
  normalizeStudioOntologyRetrievalTrace,
  validateStudioOntologyRetrievalTrace,
} from './ontology-retrieval-evidence'
import { validateActionFlowRuntimeSimulationProof } from './actionflow-runtime-simulation'

export type ApplicationResponsibilityIssueSeverity = 'error' | 'warning'

export type ApplicationResponsibilityIssueCode =
  | 'missing-generation-responsibilities'
  | 'missing-required-kind'
  | 'missing-page-decision'
  | 'missing-component-decision'
  | 'missing-datasource-decision'
  | 'missing-decision-evidence'
  | 'blocked-without-reason'
  | 'recipe-required-block-missing'
  | 'recipe-ref-not-preserved'
  | 'missing-required-asset-evidence'
  | 'asset-evidence-missing-ref'
  | 'asset-ref-unresolved'
  | 'missing-required-datasource-evidence'
  | 'datasource-evidence-missing-name'
  | 'datasource-proof-missing'
  | 'datasource-html-leak'
  | 'datasource-non-json'
  | 'datasource-empty-required'
  | 'missing-actionflow-topology'
  | 'actionflow-topology-invalid'
  | 'missing-actionflow-binding-plan'
  | 'actionflow-compiler-invalid'
  | 'missing-actionflow-runtime-simulation'
  | 'actionflow-runtime-simulation-invalid'
  | 'missing-ontology-retrieval-trace'
  | 'ontology-retrieval-invalid'

export interface ApplicationResponsibilityAuditIssue {
  code: ApplicationResponsibilityIssueCode
  severity: ApplicationResponsibilityIssueSeverity
  message: string
  partId?: string
  partKind?: ApplicationPartKind
}

export interface ApplicationResponsibilityAuditReport {
  schemaVersion: 'studio-application-responsibility-audit.v1'
  valid: boolean
  issues: ApplicationResponsibilityAuditIssue[]
  summary: {
    decisions: number
    requiredDecisions: number
    blockedDecisions: number
    requiredKinds: ApplicationPartKind[]
    coveredRequiredKinds: ApplicationPartKind[]
    missingRequiredKinds: ApplicationPartKind[]
  }
}

interface BuildApplicationPartDecisionOptions extends BuildStudioOntologyRetrievalTraceOptions {
  source?: string
}

export interface ApplicationGenerationQualitySource {
  appId?: string
  name?: string
  datasources?: unknown
  generationResponsibilities?: ApplicationPartDecision[]
  generationQuality?: StudioApplicationGenerationQuality
}

interface NormalizedDatasourceSpec {
  name: string
}

export interface ApplicationGenerationQualityProofInput {
  browserEvidence?: StudioEndUserAppBrowserEvidence | null
  endUserProof?: StudioEndUserAppGenerationProof | null
  source?: string
}

const REQUIRED_BASE_KINDS: ApplicationPartKind[] = [
  'design-system',
  'page',
  'component',
  'layout',
  'navigation',
  'copy',
  'deployment-proof',
]

function stableId(value: string | undefined, fallback: string): string {
  const input = (value ?? fallback).trim().toLowerCase()
  const slug = input
    .replace(/[^a-z0-9:/._-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '')
  return slug || fallback
}

function pagePartId(page: NonNullable<AppSpecFull['pages']>[number], index: number): string {
  return `page:${stableId(page.id || page.route, `page-${index + 1}`)}`
}

function componentPartId(block: AppSpecFull['blocks'][number], index: number): string {
  return `component:${stableId(block.name, `block-${index + 1}`)}`
}

function datasourcePartId(datasource: NonNullable<AppSpecFull['datasources']>[number], index: number): string {
  return `datasource:${stableId(datasource.name, `datasource-${index + 1}`)}`
}

function normalizeDatasourceSpecs(source: { datasources?: unknown }): NormalizedDatasourceSpec[] {
  const datasources = source.datasources
  if (Array.isArray(datasources)) {
    return datasources
      .map((datasource, index) => {
        if (!datasource || typeof datasource !== 'object') {
          return { name: `datasource-${index + 1}` }
        }
        const record = datasource as Record<string, unknown>
        return {
          name: typeof record.name === 'string' && record.name.trim()
            ? record.name
            : `datasource-${index + 1}`,
        }
      })
  }
  if (datasources && typeof datasources === 'object') {
    return Object.keys(datasources as Record<string, unknown>).map((name) => ({ name }))
  }
  return []
}

function appText(appSpec: AppSpecFull): string {
  return [
    appSpec.name,
    appSpec.description,
    ...(appSpec.blocks ?? []).flatMap((block) => [block.name, block.type, block.description]),
  ].filter(Boolean).join(' ').toLowerCase()
}

function hasGlobalState(appSpec: AppSpecFull): boolean {
  return Object.keys(appSpec.globalState ?? {}).length > 0
}

function hasActionFlowNeed(appSpec: AppSpecFull): boolean {
  return appSpec.interactionContract?.actionFlowRequired === true
    || appSpec.blocks.some((block) => (block.binds ?? []).length > 0)
}

function hasBusinessRules(appSpec: AppSpecFull): boolean {
  return appSpec.blocks.some((block) => (block.binds ?? []).length > 0)
    || (appSpec.interactionContract?.notes ?? []).length > 0
}

function hasAuthOrRoleNeed(appSpec: AppSpecFull): boolean {
  return /\b(admin|backoffice|back-office|internal|operations|enterprise|rbac|permission|role)\b/.test(appText(appSpec))
}

function hasAssetNeed(appSpec: AppSpecFull): boolean {
  return appSpec.generationQuality?.assets?.required === true
    || (appSpec.generationQuality?.assets?.items.length ?? 0) > 0
    || (appSpec.generationResponsibilities ?? []).some((decision) => (
      decision.required && decision.partKind === 'asset'
    ))
}

function hasAssetQualityNeed(appSpec: ApplicationGenerationQualitySource): boolean {
  return appSpec.generationQuality?.assets?.required === true
    || (appSpec.generationQuality?.assets?.items.length ?? 0) > 0
    || (appSpec.generationResponsibilities ?? []).some((decision) => (
      decision.required && decision.partKind === 'asset'
    ))
}

function hasDatasourceProofNeed(appSpec: ApplicationGenerationQualitySource): boolean {
  return normalizeDatasourceSpecs(appSpec).length > 0
    || appSpec.generationQuality?.datasources?.required === true
    || (appSpec.generationQuality?.datasources?.items.length ?? 0) > 0
}

function recipeForBlock(appSpec: AppSpecFull, blockName: string): string | undefined {
  return appSpec.goldenRecipePack?.recipes.find((recipe) => recipe.requiredBlocks.includes(blockName))?.id
}

function dominantRecipeRef(appSpec: AppSpecFull): string | undefined {
  return appSpec.goldenRecipePack?.recipes[0]?.id
}

function buildDecision(
  decision: Omit<ApplicationPartDecision, 'ontologyRefs' | 'evidenceRefs'> & {
    ontologyRefs?: string[]
    evidenceRefs?: string[]
  },
): ApplicationPartDecision {
  const { selectedRecipeRef, ...definedDecision } = decision
  return {
    ...definedDecision,
    ...(selectedRecipeRef !== undefined ? { selectedRecipeRef } : {}),
    ontologyRefs: decision.ontologyRefs ?? [],
    evidenceRefs: decision.evidenceRefs ?? [],
  }
}

export function buildApplicationPartDecisions(
  appSpec: AppSpecFull,
  options: BuildApplicationPartDecisionOptions = {},
): ApplicationPartDecision[] {
  const source = options.source ?? 'studio-planner'
  const decisions: ApplicationPartDecision[] = []
  const style = appSpec.themeHint?.style ?? 'default'

  const add = (
    partKind: ApplicationPartKind,
    responsibility: GenerationResponsibility,
    partId: string,
    required: boolean,
    data: Partial<ApplicationPartDecision> = {},
  ) => {
    decisions.push(buildDecision({
      partKind,
      responsibility,
      partId,
      required,
      source,
      ...data,
    }))
  }

  add('design-system', 'binding-engine', `design-system:${stableId(style, 'default')}`, true, {
    ontologyRefs: ['cogo:DesignSystemTokenSet', 'cogo:DesignToken'],
    evidenceRefs: appSpec.designSystemContract
      ? ['appSpec.designSystemContract', 'appSpec.themeHint']
      : ['appSpec.themeHint'],
    selectedVariantId: style,
  })

  add('copy', 'llm-semantic', `copy:${stableId(appSpec.appId ?? appSpec.name, 'app')}`, true, {
    ontologyRefs: ['cogo:CopyProposal', 'cogo:SemanticDecision'],
    evidenceRefs: ['prompt', 'appSpec.description'],
  })

  if (hasGlobalState(appSpec)) {
    add('data-model', 'llm-semantic', `data-model:${stableId(appSpec.appId ?? appSpec.name, 'app')}`, true, {
      ontologyRefs: ['cogo:DomainEntity', 'cogo:StateModel'],
      evidenceRefs: ['appSpec.globalState', 'prompt'],
    })
  }

  for (const [index, page] of (appSpec.pages ?? []).entries()) {
    add('page', 'recipe-compiler', pagePartId(page, index), true, {
      ontologyRefs: ['cogo:PageRecipe', 'cogo:ApplicationRecipe'],
      evidenceRefs: [`appSpec.pages.${page.id || index}`],
      selectedRecipeRef: dominantRecipeRef(appSpec),
    })
  }

  add('navigation', 'recipe-compiler', `navigation:${stableId(appSpec.appId ?? appSpec.name, 'app')}`, true, {
    ontologyRefs: ['cogo:NavigationRecipe', 'cogo:RouteGraph'],
    evidenceRefs: ['appSpec.pages'],
    selectedRecipeRef: dominantRecipeRef(appSpec),
  })

  add('layout', 'recipe-compiler', `layout:${stableId(appSpec.appId ?? appSpec.name, 'app')}`, true, {
    ontologyRefs: ['cogo:ResponsiveRecipe', 'cogo:LayoutPattern'],
    evidenceRefs: ['appSpec.pages', 'appSpec.blocks'],
    selectedRecipeRef: dominantRecipeRef(appSpec),
  })

  for (const recipe of appSpec.goldenRecipePack?.recipes ?? []) {
    add('layout', 'recipe-compiler', `recipe:${stableId(recipe.id, recipe.scenarioId)}`, true, {
      ontologyRefs: ['cogo:ApplicationRecipe', `recipe:${recipe.id}`],
      evidenceRefs: ['appSpec.goldenRecipePack'],
      selectedRecipeRef: recipe.id,
    })
  }

  for (const [index, block] of appSpec.blocks.entries()) {
    add('component', 'binding-engine', componentPartId(block, index), true, {
      ontologyRefs: ['uiox:ComponentType', `uiox:ComponentType/${block.type}`],
      evidenceRefs: [`appSpec.blocks.${block.name || index}`],
      selectedRecipeRef: recipeForBlock(appSpec, block.name),
      selectedVariantId: block.type,
    })
  }

  for (const [index, datasource] of (appSpec.datasources ?? []).entries()) {
    add('datasource', 'recipe-compiler', datasourcePartId(datasource, index), true, {
      ontologyRefs: ['cogo:DatasourceRecipe', `cogo:Datasource/${datasource.name}`],
      evidenceRefs: [`appSpec.datasources.${datasource.name || index}`],
      selectedRecipeRef: dominantRecipeRef(appSpec),
    })
  }

  if (hasAssetNeed(appSpec)) {
    add('asset', 'binding-engine', `asset:${stableId(appSpec.appId ?? appSpec.name, 'app')}`, true, {
      ontologyRefs: ['cogo:AssetRef', 'cogo:AssetManifest'],
      evidenceRefs: appSpec.generationQuality?.assets?.items.length
        ? ['appSpec.generationQuality.assets']
        : ['appSpec.blocks'],
    })
  }

  if (hasActionFlowNeed(appSpec)) {
    add('actionflow', 'recipe-compiler', `actionflow:${stableId(appSpec.appId ?? appSpec.name, 'app')}`, true, {
      ontologyRefs: ['cogo:ActionFlowRecipe', 'cogo:EventCapability', 'cogo:ExpressionPrefix'],
      evidenceRefs: ['appSpec.interactionContract', 'appSpec.blocks.binds'],
      selectedRecipeRef: dominantRecipeRef(appSpec),
    })
  }

  if (hasBusinessRules(appSpec)) {
    add('business-rule', 'llm-semantic', `business-rule:${stableId(appSpec.appId ?? appSpec.name, 'app')}`, true, {
      ontologyRefs: ['cogo:BusinessRule', 'cogo:SemanticDecision'],
      evidenceRefs: ['appSpec.interactionContract', 'appSpec.blocks.binds', 'prompt'],
    })
  }

  if (hasAuthOrRoleNeed(appSpec)) {
    add('auth', 'recipe-compiler', `auth:${stableId(appSpec.appId ?? appSpec.name, 'app')}`, true, {
      ontologyRefs: ['cogo:RolePermissionRecipe', 'cogo:NavigationGuard'],
      evidenceRefs: ['prompt', 'appSpec.description'],
      selectedRecipeRef: dominantRecipeRef(appSpec),
    })
  }

  add('deployment-proof', 'binding-engine', `deployment-proof:${stableId(appSpec.appId ?? appSpec.name, 'app')}`, true, {
    ontologyRefs: ['cogo:EvidenceRequirement', 'cogo:DeploymentReadbackProof'],
    evidenceRefs: ['studio.evidencePolicy', 'appSpec.projectPlan'],
  })

  return decisions
}

export function mergeApplicationPartDecisions(
  existing: ApplicationPartDecision[] | undefined,
  generated: ApplicationPartDecision[],
): ApplicationPartDecision[] {
  const byPartId = new Map<string, ApplicationPartDecision>()
  for (const decision of existing ?? []) {
    if (decision.partId) byPartId.set(decision.partId, decision)
  }
  for (const decision of generated) {
    if (!byPartId.has(decision.partId)) byPartId.set(decision.partId, decision)
  }
  return [...byPartId.values()]
}

export function withApplicationResponsibilityDecisions(
  appSpec: AppSpecFull,
  options: BuildApplicationPartDecisionOptions = {},
): AppSpecFull {
  const withResponsibilities = {
    ...appSpec,
    generationResponsibilities: mergeApplicationPartDecisions(
      appSpec.generationResponsibilities,
      buildApplicationPartDecisions(appSpec, options),
    ),
  }

  return {
    ...withResponsibilities,
    ontologyRetrievalTrace: appSpec.ontologyRetrievalTrace
      ? normalizeStudioOntologyRetrievalTrace(appSpec.ontologyRetrievalTrace, withResponsibilities, options)
      : buildStudioOntologyRetrievalTrace(withResponsibilities, options),
  }
}

function qualityEvidenceRef(
  refs: readonly string[] | undefined,
  fallback: string,
): string {
  return refs?.find((ref) => ref.trim().length > 0) ?? fallback
}

function collectScenarioDataEvidence(
  browserEvidence?: StudioEndUserAppBrowserEvidence | null,
  endUserProof?: StudioEndUserAppGenerationProof | null,
): string[] {
  return [
    ...(browserEvidence?.scenarioEvidence ?? []).flatMap((scenario) => [...(scenario.dataEvidence ?? [])]),
    ...(endUserProof?.scenarioMatrix.scenarios ?? []).flatMap((scenario) => scenario.dataEvidence),
  ]
}

function collectScenarioArtifactEvidence(
  browserEvidence?: StudioEndUserAppBrowserEvidence | null,
  endUserProof?: StudioEndUserAppGenerationProof | null,
): string[] {
  return [
    ...(browserEvidence?.scenarioEvidence ?? []).flatMap((scenario) => [...(scenario.artifactEvidence ?? [])]),
    ...(endUserProof?.scenarioMatrix.scenarios ?? []).flatMap((scenario) => scenario.artifactEvidence),
  ]
}

function evidenceMentionsDatasource(evidence: readonly string[], datasourceName: string, datasourceCount: number): boolean {
  if (evidence.length === 0) return false
  const normalized = datasourceName.toLowerCase()
  return evidence.some((entry) => entry.toLowerCase().includes(normalized))
    || (datasourceCount === 1 && evidence.length > 0)
}

function inferSampleRowCount(evidence: readonly string[]): number {
  for (const entry of evidence) {
    const match = entry.match(/\b(\d+)\s*(rows?|records?|items?)\b/i)
    if (match) return Number(match[1])
  }
  return evidence.length > 0 ? 1 : 0
}

function mergeAssetQualityItems(
  existing: readonly StudioApplicationAssetQualityEvidence[] | undefined,
  generated: StudioApplicationAssetQualityEvidence[],
): StudioApplicationAssetQualityEvidence[] {
  const byRef = new Map<string, StudioApplicationAssetQualityEvidence>()
  for (const item of existing ?? []) {
    byRef.set(item.assetRef, item)
  }
  for (const item of generated) {
    if (!byRef.has(item.assetRef)) byRef.set(item.assetRef, item)
  }
  return [...byRef.values()]
}

function mergeDatasourceQualityItems(
  existing: readonly StudioApplicationDatasourceQualityEvidence[] | undefined,
  generated: StudioApplicationDatasourceQualityEvidence[],
): StudioApplicationDatasourceQualityEvidence[] {
  const byName = new Map<string, StudioApplicationDatasourceQualityEvidence>()
  for (const item of existing ?? []) {
    byName.set(item.datasourceName, item)
  }
  for (const item of generated) {
    if (!byName.has(item.datasourceName)) byName.set(item.datasourceName, item)
  }
  return [...byName.values()]
}

function buildAssetQualityItemsFromProof(
  appSpec: ApplicationGenerationQualitySource,
  input: ApplicationGenerationQualityProofInput,
): StudioApplicationAssetQualityEvidence[] {
  const source = input.source ?? 'studio-browser-proof'
  const screenshotArtifacts = input.browserEvidence?.screenshotArtifacts ?? []
  const artifactEvidence = collectScenarioArtifactEvidence(input.browserEvidence, input.endUserProof)
  const screenshotArtifactCount = screenshotArtifacts.length || input.endUserProof?.quality.screenshotArtifactCount || 0
  const brokenImageCount = input.browserEvidence?.brokenImageCount ?? input.endUserProof?.quality.brokenImageCount ?? null
  const required = hasAssetQualityNeed(appSpec)
  const evidenceRef = qualityEvidenceRef(
    [...screenshotArtifacts, ...artifactEvidence],
    'generationEvidence.endUserAppProof.quality',
  )

  if (brokenImageCount !== null && brokenImageCount > 0) {
    return [{
      assetRef: 'browser:broken-images',
      status: 'broken',
      evidenceRef,
      source,
    }]
  }

  if (!required) return []

  if (screenshotArtifactCount > 0) {
    return [{
      assetRef: 'browser:rendered-screenshots',
      status: 'loaded',
      evidenceRef,
      source,
    }]
  }

  return [{
    assetRef: 'browser:rendered-assets',
    status: 'missing',
    evidenceRef,
    source,
  }]
}

function buildDatasourceQualityItemsFromProof(
  appSpec: ApplicationGenerationQualitySource,
  input: ApplicationGenerationQualityProofInput,
): StudioApplicationDatasourceQualityEvidence[] {
  const source = input.source ?? 'studio-browser-proof'
  const datasources = normalizeDatasourceSpecs(appSpec)
  const dataEvidence = collectScenarioDataEvidence(input.browserEvidence, input.endUserProof)
  const required = hasDatasourceProofNeed(appSpec)

  if (datasources.length === 0) {
    const hasProof = dataEvidence.length > 0
    return required
      ? [{
          datasourceName: 'app:datasource-proof',
          status: hasProof ? 'live-json' : 'missing',
          responseKind: hasProof ? 'json' : 'unknown',
          ...(hasProof ? { sampleRowCount: inferSampleRowCount(dataEvidence) } : {}),
          evidenceRef: qualityEvidenceRef(dataEvidence, 'generationEvidence.endUserAppProof.scenarioMatrix'),
          source,
        }]
      : []
  }

  return datasources.map((datasource) => {
    const hasProof = evidenceMentionsDatasource(dataEvidence, datasource.name, datasources.length)
    const matchingEvidence = dataEvidence.filter((entry) => (
      entry.toLowerCase().includes(datasource.name.toLowerCase())
    ))
    const evidenceForDatasource = matchingEvidence.length > 0 ? matchingEvidence : dataEvidence
    return {
      datasourceName: datasource.name,
      status: hasProof ? 'live-json' : 'missing',
      responseKind: hasProof ? 'json' : 'unknown',
      ...(hasProof ? { sampleRowCount: Math.max(1, inferSampleRowCount(evidenceForDatasource)) } : {}),
      evidenceRef: qualityEvidenceRef(evidenceForDatasource, 'generationEvidence.endUserAppProof.scenarioMatrix'),
      source,
    }
  })
}

export function buildApplicationGenerationQualityFromProof(
  appSpec: ApplicationGenerationQualitySource,
  input: ApplicationGenerationQualityProofInput,
): StudioApplicationGenerationQuality {
  const existing = appSpec.generationQuality
  const assetItems = mergeAssetQualityItems(
    existing?.assets?.items,
    buildAssetQualityItemsFromProof(appSpec, input),
  )
  const datasourceItems = mergeDatasourceQualityItems(
    existing?.datasources?.items,
    buildDatasourceQualityItemsFromProof(appSpec, input),
  )

  return {
    schemaVersion: 'studio-application-generation-quality.v1',
    ...(existing?.assets || assetItems.length > 0
      ? {
          assets: {
            required: existing?.assets?.required ?? hasAssetQualityNeed(appSpec),
            items: assetItems,
          },
        }
      : {}),
    ...(existing?.datasources || datasourceItems.length > 0
      ? {
          datasources: {
            required: existing?.datasources?.required ?? hasDatasourceProofNeed(appSpec),
            items: datasourceItems,
          },
        }
      : {}),
  }
}

export function withApplicationGenerationQualityFromProof<T extends ApplicationGenerationQualitySource>(
  appSpec: T,
  input: ApplicationGenerationQualityProofInput,
): T & { generationQuality: StudioApplicationGenerationQuality } {
  return {
    ...appSpec,
    generationQuality: buildApplicationGenerationQualityFromProof(appSpec, input),
  }
}

export function inferRequiredApplicationPartKinds(appSpec: AppSpecFull): ApplicationPartKind[] {
  const required = new Set<ApplicationPartKind>(REQUIRED_BASE_KINDS)
  if (hasGlobalState(appSpec)) required.add('data-model')
  if (normalizeDatasourceSpecs(appSpec).length > 0) required.add('datasource')
  if (hasAssetNeed(appSpec)) required.add('asset')
  if (hasActionFlowNeed(appSpec)) required.add('actionflow')
  if (hasBusinessRules(appSpec)) required.add('business-rule')
  if (hasAuthOrRoleNeed(appSpec)) required.add('auth')
  return [...required]
}

function isResolvedAssetStatus(status: string): boolean {
  return status === 'packaged' || status === 'reachable' || status === 'loaded'
}

function isValidDatasourceStatus(status: string): boolean {
  return status === 'seeded' || status === 'live-json' || status === 'mock-json'
}

function assertApplicationAssetQuality(
  appSpec: AppSpecFull,
  addIssue: (issue: ApplicationResponsibilityAuditIssue) => void,
): void {
  if (!hasAssetNeed(appSpec)) return

  const assetItems = appSpec.generationQuality?.assets?.items ?? []
  if (assetItems.length === 0) {
    addIssue({
      code: 'missing-required-asset-evidence',
      severity: 'error',
      partKind: 'asset',
      message: 'Required asset binding needs packaged/reachable asset evidence before generation can pass.',
    })
    return
  }

  for (const item of assetItems) {
    if (!item.assetRef) {
      addIssue({
        code: 'asset-evidence-missing-ref',
        severity: 'error',
        partKind: 'asset',
        message: 'Asset quality evidence must include assetRef.',
      })
      continue
    }
    if (!isResolvedAssetStatus(item.status)) {
      addIssue({
        code: 'asset-ref-unresolved',
        severity: 'error',
        partId: `asset:${item.assetRef}`,
        partKind: 'asset',
        message: `Asset "${item.assetRef}" is ${item.status}; packaged/reachable/loaded evidence is required.`,
      })
    }
  }
}

function assertApplicationDatasourceQuality(
  appSpec: AppSpecFull,
  addIssue: (issue: ApplicationResponsibilityAuditIssue) => void,
): void {
  if (!hasDatasourceProofNeed(appSpec)) return

  const datasourceItems = appSpec.generationQuality?.datasources?.items ?? []
  if (datasourceItems.length === 0) {
    addIssue({
      code: 'missing-required-datasource-evidence',
      severity: 'error',
      partKind: 'datasource',
      message: 'Declared datasources need seeded/live JSON proof before generation can pass.',
    })
  }

  const evidenceByName = new Map(
    datasourceItems
      .filter((item) => item.datasourceName)
      .map((item) => [item.datasourceName, item]),
  )

  for (const item of datasourceItems) {
    if (!item.datasourceName) {
      addIssue({
        code: 'datasource-evidence-missing-name',
        severity: 'error',
        partKind: 'datasource',
        message: 'Datasource quality evidence must include datasourceName.',
      })
      continue
    }
    if (item.status === 'html-leak' || item.responseKind === 'html') {
      addIssue({
        code: 'datasource-html-leak',
        severity: 'error',
        partId: `datasource:${item.datasourceName}`,
        partKind: 'datasource',
        message: `Datasource "${item.datasourceName}" captured HTML/dev-server output instead of JSON.`,
      })
      continue
    }
    if (item.status === 'non-json' || item.responseKind === 'text') {
      addIssue({
        code: 'datasource-non-json',
        severity: 'error',
        partId: `datasource:${item.datasourceName}`,
        partKind: 'datasource',
        message: `Datasource "${item.datasourceName}" did not prove a JSON response.`,
      })
      continue
    }
    if (item.status === 'empty-required' || item.sampleRowCount === 0) {
      addIssue({
        code: 'datasource-empty-required',
        severity: 'error',
        partId: `datasource:${item.datasourceName}`,
        partKind: 'datasource',
        message: `Datasource "${item.datasourceName}" has no required sample rows.`,
      })
      continue
    }
    if (!isValidDatasourceStatus(item.status)) {
      addIssue({
        code: 'datasource-proof-missing',
        severity: 'error',
        partId: `datasource:${item.datasourceName}`,
        partKind: 'datasource',
        message: `Datasource "${item.datasourceName}" status is ${item.status}; seeded/live-json/mock-json proof is required.`,
      })
    }
  }

  for (const datasource of normalizeDatasourceSpecs(appSpec)) {
    if (!evidenceByName.has(datasource.name)) {
      addIssue({
        code: 'datasource-proof-missing',
        severity: 'error',
        partId: `datasource:${datasource.name}`,
        partKind: 'datasource',
        message: `Datasource "${datasource.name}" is declared but has no quality proof.`,
      })
    }
  }
}

function assertApplicationActionFlowTopology(
  appSpec: AppSpecFull,
  addIssue: (issue: ApplicationResponsibilityAuditIssue) => void,
): void {
  if (!hasActionFlowNeed(appSpec)) return

  if (!appSpec.actionFlowTopology) {
    addIssue({
      code: 'missing-actionflow-topology',
      severity: 'error',
      partKind: 'actionflow',
      message: 'ActionFlow generation requires a state topology contract before flow compilation can pass.',
    })
    return
  }

  const proof = appSpec.actionFlowValidationProof ?? validateActionFlowTopologyContract(
    appSpec.actionFlowTopology,
    appSpec,
  )
  if (!proof.valid) {
    addIssue({
      code: 'actionflow-topology-invalid',
      severity: 'error',
      partKind: 'actionflow',
      message: `ActionFlow state topology is invalid: ${proof.issues.map((issue) => issue.code).join(', ')}`,
    })
    return
  }

  if (!appSpec.actionFlowBindingPlan || !appSpec.actionFlowCompiledFlows) {
    addIssue({
      code: 'missing-actionflow-binding-plan',
      severity: 'error',
      partKind: 'actionflow',
      message: 'ActionFlow generation requires a binding plan and compiled flow map before compilation can pass.',
    })
    return
  }

  const compilerProof = appSpec.actionFlowCompilerProof ?? validateActionFlowCompilerArtifacts(
    appSpec,
    appSpec.actionFlowTopology,
    appSpec.actionFlowBindingPlan,
    appSpec.actionFlowCompiledFlows,
  )
  if (!compilerProof.valid) {
    addIssue({
      code: 'actionflow-compiler-invalid',
      severity: 'error',
      partKind: 'actionflow',
      message: `ActionFlow compiler artifacts are invalid: ${compilerProof.issues.map((issue) => issue.code).join(', ')}`,
    })
    return
  }

  if (!appSpec.actionFlowRuntimeSimulationProof) {
    addIssue({
      code: 'missing-actionflow-runtime-simulation',
      severity: 'error',
      partKind: 'actionflow',
      message: 'ActionFlow generation requires a deterministic runtime simulation proof before preview/publish can pass.',
    })
    return
  }

  const runtimeProof = validateActionFlowRuntimeSimulationProof(appSpec.actionFlowRuntimeSimulationProof)
  if (!runtimeProof.valid) {
    addIssue({
      code: 'actionflow-runtime-simulation-invalid',
      severity: 'error',
      partKind: 'actionflow',
      message: `ActionFlow runtime simulation is invalid: ${runtimeProof.issues.map((issue) => issue.code).join(', ')}`,
    })
  }
}

function assertApplicationOntologyRetrievalTrace(
  appSpec: AppSpecFull,
  addIssue: (issue: ApplicationResponsibilityAuditIssue) => void,
): void {
  const validation = validateStudioOntologyRetrievalTrace(appSpec.ontologyRetrievalTrace)
  for (const issue of validation.issues) {
    addIssue({
      code: issue.code === 'missing-ontology-retrieval-trace'
        ? 'missing-ontology-retrieval-trace'
        : 'ontology-retrieval-invalid',
      severity: issue.severity,
      message: issue.message,
    })
  }
}

export function assertApplicationResponsibilityCoverage(appSpec: AppSpecFull): ApplicationResponsibilityAuditReport {
  const decisions = appSpec.generationResponsibilities ?? []
  const issues: ApplicationResponsibilityAuditIssue[] = []
  const requiredKinds = inferRequiredApplicationPartKinds(appSpec)
  const coveredRequiredKinds = new Set<ApplicationPartKind>(
    decisions
      .filter((decision) => decision.required)
      .map((decision) => decision.partKind),
  )

  const addIssue = (issue: ApplicationResponsibilityAuditIssue) => {
    issues.push(issue)
  }

  if (decisions.length === 0) {
    addIssue({
      code: 'missing-generation-responsibilities',
      severity: 'error',
      message: 'AppSpec must classify generation responsibilities before app generation.',
    })
  }

  for (const partKind of requiredKinds) {
    if (!coveredRequiredKinds.has(partKind)) {
      addIssue({
        code: 'missing-required-kind',
        severity: 'error',
        partKind,
        message: `Required application part kind "${partKind}" is not classified.`,
      })
    }
  }

  for (const [index, page] of (appSpec.pages ?? []).entries()) {
    const partId = pagePartId(page, index)
    if (!decisions.some((decision) => decision.partId === partId && decision.partKind === 'page')) {
      addIssue({
        code: 'missing-page-decision',
        severity: 'error',
        partId,
        partKind: 'page',
        message: `Page "${page.id || page.route}" has no responsibility decision.`,
      })
    }
  }

  for (const [index, block] of appSpec.blocks.entries()) {
    const partId = componentPartId(block, index)
    if (!decisions.some((decision) => decision.partId === partId && decision.partKind === 'component')) {
      addIssue({
        code: 'missing-component-decision',
        severity: 'error',
        partId,
        partKind: 'component',
        message: `Block "${block.name}" has no component binding decision.`,
      })
    }
  }

  for (const [index, datasource] of (appSpec.datasources ?? []).entries()) {
    const partId = datasourcePartId(datasource, index)
    if (!decisions.some((decision) => decision.partId === partId && decision.partKind === 'datasource')) {
      addIssue({
        code: 'missing-datasource-decision',
        severity: 'error',
        partId,
        partKind: 'datasource',
        message: `Datasource "${datasource.name}" has no recipe/compiler decision.`,
      })
    }
  }

  for (const decision of decisions) {
    if (decision.required && decision.responsibility !== 'blocked') {
      if (decision.ontologyRefs.length === 0 || decision.evidenceRefs.length === 0) {
        addIssue({
          code: 'missing-decision-evidence',
          severity: 'error',
          partId: decision.partId,
          partKind: decision.partKind,
          message: `Required decision "${decision.partId}" must preserve ontology and evidence refs.`,
        })
      }
    }
    if (decision.responsibility === 'blocked' && !decision.blockedReason) {
      addIssue({
        code: 'blocked-without-reason',
        severity: 'error',
        partId: decision.partId,
        partKind: decision.partKind,
        message: `Blocked decision "${decision.partId}" must explain why generation cannot continue.`,
      })
    }
  }

  for (const recipe of appSpec.goldenRecipePack?.recipes ?? []) {
    const blockNames = new Set(appSpec.blocks.map((block) => block.name))
    for (const requiredBlock of recipe.requiredBlocks) {
      if (!blockNames.has(requiredBlock)) {
        addIssue({
          code: 'recipe-required-block-missing',
          severity: 'error',
          partKind: 'component',
          message: `Recipe "${recipe.id}" requires block "${requiredBlock}", but the AppSpec does not include it.`,
        })
      }
    }
    if (!decisions.some((decision) => decision.selectedRecipeRef === recipe.id)) {
      addIssue({
        code: 'recipe-ref-not-preserved',
        severity: 'error',
        partKind: 'layout',
        message: `Recipe "${recipe.id}" is selected but no responsibility decision preserves it.`,
      })
    }
  }

  assertApplicationAssetQuality(appSpec, addIssue)
  assertApplicationDatasourceQuality(appSpec, addIssue)
  assertApplicationOntologyRetrievalTrace(appSpec, addIssue)
  assertApplicationActionFlowTopology(appSpec, addIssue)

  const missingRequiredKinds = requiredKinds.filter((kind) => !coveredRequiredKinds.has(kind))
  return {
    schemaVersion: 'studio-application-responsibility-audit.v1',
    valid: !issues.some((issue) => issue.severity === 'error'),
    issues,
    summary: {
      decisions: decisions.length,
      requiredDecisions: decisions.filter((decision) => decision.required).length,
      blockedDecisions: decisions.filter((decision) => decision.responsibility === 'blocked').length,
      requiredKinds,
      coveredRequiredKinds: [...coveredRequiredKinds],
      missingRequiredKinds,
    },
  }
}
